/**
 * useQualityWeights — the weights of the six quality criteria of a project
 * (#874, spec docs/plans/2026-10-05-quality-weights.md §8): its own resource,
 * its own save and its own confirmation, apart from the tab's shared Save
 * (that one sends a single PATCH of the project row).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QualityWeights, QualityWeightsResponse } from '@aica/api-types/schemas';
import { useApiClient } from '@/hooks/useApiClient';
import { isIdentityQuery } from '@/lib/access-revision';
import { ApiError } from '@/lib/fetch-utils';
import { QUALITY_WEIGHTS_CONFLICT_CODE } from '@/lib/api/domains/projects.api';

export function qualityWeightsQueryKey(projectId: string) {
  return ['project', projectId, 'quality-weights'] as const;
}

export function useQualityWeights(projectId: string) {
  const { apiClient } = useApiClient();
  return useQuery({
    queryKey: qualityWeightsQueryKey(projectId),
    queryFn: () => apiClient.projects.getQualityWeights(projectId),
    enabled: !!projectId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useQualityWeightsPreview(projectId: string) {
  const { apiClient } = useApiClient();
  return useMutation({
    mutationFn: (weights: QualityWeights | null) =>
      apiClient.projects.previewQualityWeights(projectId, weights),
  });
}

export interface SaveQualityWeightsInput {
  weights: QualityWeights | null;
  /** The weights the editor started from (what the load returned). */
  expected: QualityWeights | null;
}

/** 409 of the PUT: the project's weights changed since the editor loaded them. */
export function isQualityWeightsConflict(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    (error.data as { code?: unknown } | null | undefined)?.code ===
      QUALITY_WEIGHTS_CONFLICT_CODE
  );
}

export function useSaveQualityWeights(projectId: string) {
  const { apiClient } = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ weights, expected }: SaveQualityWeightsInput) =>
      apiClient.projects.updateQualityWeights(projectId, weights, expected),
    onSuccess: (saved: QualityWeightsResponse) => {
      const key = qualityWeightsQueryKey(projectId);
      queryClient.setQueryData(key, saved);
      // New weights rescore the project's whole history, and the score is
      // carried by too many caches to list (the call card, the call list,
      // every dashboard, rating and report): they are marked stale, so the
      // next screen that mounts one refetches it instead of showing the old
      // score as current. Nothing refetches now — the settings screen shows
      // no score — and the identity queries are not touched at all. Endpoints
      // with an HTTP cache (reports) may still answer from it for a while;
      // the confirmation says so (`qualityTab.weights.confirm.refresh`).
      void queryClient.invalidateQueries({
        refetchType: 'none',
        predicate: (query) =>
          !isIdentityQuery(query.queryKey) &&
          JSON.stringify(query.queryKey) !== JSON.stringify(key),
      });
    },
    onError: (error) => {
      // Someone saved in between: reload what is stored now.
      if (isQualityWeightsConflict(error)) {
        void queryClient.invalidateQueries({
          queryKey: qualityWeightsQueryKey(projectId),
        });
      }
    },
  });
}
